import { Navigate, Outlet, useLocation } from "react-router";
import { useAuth } from "../auth/AuthProvider";
import { ErrorState } from "../components/states";
import { signOutTarget } from "../lib/signOutTarget";
import { Splash } from "./Root";

const NEXT_KEY = "sk:next";

export function rememberNext(path: string) {
  try {
    sessionStorage.setItem(NEXT_KEY, path);
  } catch {
    // ignore
  }
}

/** Consume the remembered destination (used after verification / onboarding). */
export function takeNext(fallback = "/recipes"): string {
  try {
    const next = sessionStorage.getItem(NEXT_KEY);
    sessionStorage.removeItem(NEXT_KEY);
    if (next && next.startsWith("/") && !next.startsWith("//")) return next;
  } catch {
    // ignore
  }
  return fallback;
}

export function safeNext(value: string | null, fallback = "/recipes") {
  if (value && value.startsWith("/") && !value.startsWith("//") && !value.startsWith("/login")) return value;
  return fallback;
}

/** Carry the interrupted local route through auth screens, including its query string. */
export function withNext(path: string, next: string | null) {
  return next ? `${path}${path.includes("?") ? "&" : "?"}next=${encodeURIComponent(safeNext(next))}` : path;
}

/** Signed-in Firebase user required. */
export function RequireUser() {
  const { user, initializing } = useAuth();
  const location = useLocation();
  if (initializing) return <Splash />;
  if (!user) {
    if (signOutTarget.current) return <Navigate to={signOutTarget.current} replace />;
    const next = `${location.pathname}${location.search}`;
    rememberNext(next);
    return <Navigate to={`/login?next=${encodeURIComponent(next)}`} replace />;
  }
  return <Outlet />;
}

/** Verified email required (the server also enforces this). */
export function RequireVerified() {
  const { user } = useAuth();
  const location = useLocation();
  if (user && !user.emailVerified) {
    rememberNext(`${location.pathname}${location.search}`);
    return <Navigate to={withNext("/verify-email", `${location.pathname}${location.search}`)} replace />;
  }
  return <Outlet />;
}

/** At least one household; otherwise onboarding. */
export function RequireHousehold() {
  const { me, meLoading, meError, refreshMe, householdId } = useAuth();
  const location = useLocation();
  if (meLoading) return <Splash label="Loading your kitchen" />;
  if (!me) {
    return (
      <main className="splash" id="main">
        <ErrorState error={meError} onRetry={() => void refreshMe()} />
      </main>
    );
  }
  if (!me.households.length || !householdId) {
    rememberNext(`${location.pathname}${location.search}`);
    return <Navigate to="/welcome" replace />;
  }
  return <Outlet />;
}

/** Entry: send people where they belong. */
export function HomeRedirect() {
  const { user, initializing } = useAuth();
  if (initializing) return <Splash />;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to="/recipes" replace />;
}
